import {
  ChevronDownIcon,
  InfoCircleIcon,
  SearchMagnifyingGlassIcon,
  Span,
  Text,
} from '@/components/atoms'
import { useTranslation } from 'react-i18next'
import { useSelectField } from './use-select-field'

export interface SelectOption {
  value: string
  label: string
}

type SelectVariant = 'default' | 'calendar'

const TRIGGER_BASE: Record<SelectVariant, string> = {
  default:
    'flex h-12 w-full items-center rounded-lg border border-neutral-300  px-4 pr-10 font-sans text-base tracking-[0.32px] transition outline-none',
  calendar:
    'flex h-9 w-fit items-center rounded-lg border border-transparent px-1.5 pr-4 font-sans text-base tracking-[0.32px] transition outline-none',
}

const OPTION_CLASSES: Record<SelectVariant, string> = {
  default:
    'cursor-pointer px-4 py-2 text-base text-white transition outline-none',
  calendar:
    'cursor-pointer px-2 py-1 text-xs text-white transition outline-none',
}

const CHEVRON_CLASSES: Record<SelectVariant, string> = {
  default:
    'pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2 transition-transform duration-200',
  calendar:
    'pointer-events-none absolute top-1/2 right-1 size-3.5 -translate-y-1/2 transition-transform duration-200',
}

interface SelectFieldProps {
  id?: string
  label?: string
  value: string
  options: SelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  optionsPlaceholder?: string
  helpText?: string
  error?: boolean | string
  tooltip?: string
  optional?: boolean
  searchable?: boolean
  disabled?: boolean
  variant?: SelectVariant
  dropdownPosition?: 'absolute' | 'relative'
  loading?: boolean
  onSearch?: (term: string) => void
  onSelect: (value: string) => void
  onOpenChange?: (open: boolean) => void
  onBlur?: () => void
  'data-testid'?: string
}

export function SelectField({
  id,
  label,
  value,
  options,
  placeholder,
  searchPlaceholder,
  optionsPlaceholder,
  helpText,
  error,
  tooltip,
  optional,
  searchable = false,
  disabled = false,
  variant = 'default',
  dropdownPosition = 'absolute',
  loading = false,
  onSearch,
  onSelect,
  onOpenChange,
  onBlur,
  'data-testid': testId,
}: SelectFieldProps) {
  const { t } = useTranslation()
  const {
    ref,
    isOpen,
    toggle,
    select,
    handleKeyDown,
    handleSearchKeyDown,
    search,
    handleSearchChange,
    searchRef,
    optionRefs,
    selectedLabel,
    filteredOptions,
    showDropdown,
  } = useSelectField({
    onBlur,
    onOpenChange,
    onSearch,
    options,
    value,
    searchable,
    loading,
    hasOptionsPlaceholder: !!optionsPlaceholder,
  })

  const hasError = !!error
  const errorMessage = typeof error === 'string' ? error : undefined
  const displayedHelp = errorMessage ?? helpText

  const chevronColor = disabled
    ? 'text-neutral-300'
    : hasError
      ? 'text-error-base'
      : 'text-indigo-base'

  const triggerClasses = [
    TRIGGER_BASE[variant],
    disabled
      ? 'cursor-not-allowed border-neutral-300 bg-neutral-50'
      : hasError
        ? 'border-error-base focus:border-error-base bg-error-50'
        : isOpen
          ? '!border-indigo-600 bg-white'
          : 'bg-white hover:border-indigo-600 focus:border-indigo-600',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="flex w-full flex-col gap-3">
      {label && (
        <div className="flex items-center gap-1">
          <Text as="label" htmlFor={id} className="text-neutral-900">
            {label}
            {optional && (
              <Text as="span" className="text-neutral-300">
                {` (${t('common.optional')})`}
              </Text>
            )}
          </Text>
          {tooltip && (
            <div className="tooltip" data-tip={tooltip}>
              <InfoCircleIcon className="text-indigo-base h-4 w-4" />
            </div>
          )}
        </div>
      )}

      <div ref={ref} className="relative">
        {searchable && isOpen ? (
          <input
            ref={searchRef}
            id={id}
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={searchPlaceholder ?? t('common.search')}
            className="tracking-base h-12 w-full rounded-lg border border-indigo-600 bg-white px-4 py-3 pr-10 font-sans text-base outline-none placeholder:text-neutral-300"
            onKeyDown={(e) => handleSearchKeyDown(e, onSelect, filteredOptions)}
          />
        ) : (
          <button
            id={id}
            type="button"
            data-testid={testId}
            disabled={disabled}
            className={triggerClasses}
            onClick={toggle}
          >
            <span
              className={`line-clamp-1 flex-1 text-left ${selectedLabel ? 'text-neutral-900' : 'text-neutral-300'}`}
            >
              {selectedLabel ?? placeholder}
            </span>
          </button>
        )}

        {showDropdown && (
          <ul
            className={`scrollbar-indigo ${dropdownPosition} right-0 left-0 z-(--z-dropdown) mt-1 max-h-32 overflow-auto rounded-lg border border-indigo-800 bg-indigo-600 shadow-lg`}
          >
            {filteredOptions.length === 0 ? (
              search.trim() && !loading ? (
                <li className={OPTION_CLASSES[variant] + ' text-white/60'}>
                  {t('common.noResults')}
                </li>
              ) : optionsPlaceholder ? (
                <li className={OPTION_CLASSES[variant] + ' text-white/60'}>
                  {optionsPlaceholder}
                </li>
              ) : (
                <></>
              )
            ) : (
              filteredOptions.map((option, i) => (
                <li
                  key={option.value}
                  ref={(el) => {
                    optionRefs.current[i] = el ?? undefined
                  }}
                  role="option"
                  aria-selected={value === option.value}
                  tabIndex={0}
                  className={`${OPTION_CLASSES[variant]} ${
                    value === option.value
                      ? 'bg-indigo-800'
                      : 'hover:bg-indigo-base focus:bg-indigo-base'
                  }`}
                  onClick={() => select(onSelect, option.value)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, onSelect, option.value, filteredOptions, i)
                  }
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        )}

        {loading ? (
          <span
            className={`loading loading-spinner loading-xs text-indigo-base pointer-events-none absolute top-1/2 -translate-y-1/2 ${variant === 'calendar' ? 'right-1' : 'right-3'}`}
          />
        ) : searchable ? (
          <SearchMagnifyingGlassIcon
            className={`${isOpen ? 'text-indigo-base' : 'text-neutral-300'} ${CHEVRON_CLASSES[variant]}`}
          />
        ) : (
          <ChevronDownIcon
            className={`${chevronColor} ${CHEVRON_CLASSES[variant]} ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          />
        )}
      </div>

      {displayedHelp && (
        <Span className={hasError ? 'text-error-base' : 'text-neutral-900'}>
          {displayedHelp}
        </Span>
      )}
    </div>
  )
}
